import { useMutation } from "@apollo/client";
import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  DragOverlay,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent
} from '@dnd-kit/core';
import { arraySwap, SortableContext, rectSwappingStrategy } from '@dnd-kit/sortable';
import Grid from './components/Grid';
import SpecialSortableItem from './components/SpecialSortableItem';
import SpecialItem from './components/SpecialItem';
import VictoryModal from "./components/VictoryModal";
import { useLocalStorage } from "./utils/useLocalStorage";
import { SUBMIT_USER_GAME } from "./graphql/Mutations";
import HelpModal from "./components/HelpModal";
import GreetingModal from "./components/GreetingModal";
import NavigationBar from "./components/NavigationBar";
import useImage from "./utils/useImage";
import { Button } from "react-bootstrap";
import { trackEvent } from "./utils/analytics";
import { formatTime, getGameNumber, getSolutionsFontColor, getTodaysDate, getUserId } from "./utils/gameUtils";
import ErrorModal from "./components/ErrorModal";
import confetti from 'canvas-confetti';
import OfferModal from "./components/OfferModal";

// Hardcoded tiles and winning order
const ALL_TILES = ['tile1.webp', 'tile2.webp', 'tile3.webp', 'tile4.webp', 'tile5.webp', 'tile6.webp', 'tile7.webp', 'tile8.webp', 'tile9.webp'];
const WINNING_ORDER = ['tile1.webp', 'tile2.webp', 'tile3.webp', 'tile4.webp', 'tile5.webp', 'tile6.webp', 'tile7.webp', 'tile8.webp', 'tile9.webp'];

export default function SpecialPuzzle() {
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
  const [gameTiles, setGameTiles] = useState<string[]>([]);
  const [solutionTiles, setSolutionTiles] = useState<string[]>(WINNING_ORDER);
  const [activeId, setActiveId] = useState(null);
  const [activeGameDate] = useState<string>(getTodaysDate());
  const [gameExists] = useState<boolean>(true);
  const [modalState, setModalState] = useState<string>('greeting');
  const goToAdminViaShortcut = useCallback(() => {
    const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
    window.location.assign(base ? `${base}/admin` : '/admin');
  }, []);
  const offerClaimRef = useRef(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [lastHintUsed, setLastHintUsed] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [movesPlayed, setMovesPlayed] = useState(0);
  const [numSolutions, setNumSolutions] = useState(1);
  const [mode, setMode] = useState<string>('classic');

  const { getItem: getStoredUserId, setItem: setStoredUserId } = useLocalStorage('userId');

  const { getItem: getStoredLatestClassicDate, setItem: setStoredLatestClassicDate } = useLocalStorage('specialLatestClassicDate');
  const { getItem: getStoredClassicTileOrder, setItem: setStoredClassicTileOrder} = useLocalStorage('specialClassicTileOrder');
  const { getItem: getStoredClassicCompleted, setItem: setStoredClassicCompleted } = useLocalStorage('specialClassicCompleted');
  const { getItem: getStoredClassicElapsedTime, setItem: setStoredClassicElapsedTime } = useLocalStorage('specialClassicElapsedTime');
  const { getItem: getStoredClassicStreak, setItem: setStoredClassicStreak } = useLocalStorage('specialClassicStreak');
  const { getItem: getStoredClassicMaxStreak, setItem: setStoredClassicMaxStreak } = useLocalStorage('specialClassicMaxStreak');
  const { getItem: getStoredClassicWins, setItem: setStoredClassicWins } = useLocalStorage('specialClassicWins');
  const { getItem: getStoredClassicHintsUsed, setItem: setStoredClassicHintsUsed } = useLocalStorage('specialClassicHintsUsed');
  const { getItem: getStoredClassicLastHintUsed, setItem: setStoredClassicLastHintUsed } = useLocalStorage('specialClassicLastHintUsed');
  const { getItem: getStoredClassicMovesPlayed, setItem: setStoredClassicMovesPlayed } = useLocalStorage('specialClassicMovesPlayed');
  
  const { getItem: getStoredLatestCasualDate, setItem: setStoredLatestCasualDate } = useLocalStorage('specialLatestCasualDate');
  const { getItem: getStoredCasualTileOrder, setItem: setStoredCasualTileOrder} = useLocalStorage('specialCasualTileOrder');
  const { getItem: getStoredCasualCompleted, setItem: setStoredCasualCompleted } = useLocalStorage('specialCasualCompleted');
  const { getItem: getStoredCasualElapsedTime, setItem: setStoredCasualElapsedTime } = useLocalStorage('specialCasualElapsedTime');
  const { getItem: getStoredCasualStreak, setItem: setStoredCasualStreak } = useLocalStorage('specialCasualStreak');
  const { getItem: getStoredCasualMaxStreak, setItem: setStoredCasualMaxStreak } = useLocalStorage('specialCasualMaxStreak');
  const { getItem: getStoredCasualWins, setItem: setStoredCasualWins } = useLocalStorage('specialCasualWins');
  const { getItem: getStoredCasualHintsUsed, setItem: setStoredCasualHintsUsed } = useLocalStorage('specialCasualHintsUsed');
  const { getItem: getStoredCasualLastHintUsed, setItem: setStoredCasualLastHintUsed } = useLocalStorage('specialCasualLastHintUsed');
  const { getItem: getStoredCasualMovesPlayed, setItem: setStoredCasualMovesPlayed } = useLocalStorage('specialCasualMovesPlayed');

  const { image: greetingIconImage } = useImage("tile1.webp");
  const { image: howToImg } = useImage("how-to.png")
  const { image: helpIcon } = useImage("help.png")
  const { image: backIcon } = useImage("back.png")
  const { image: igIcon } = useImage("instagram.png")
  const { image: statsIcon } = useImage("stats.png")

  const hintLength = 15;

  type StorageFunctions = {
    getStoredCompleted: () => boolean;
    setStoredElapsedTime: (value: number) => void;
    setStoredMovesPlayed: (value: number) => void;
    setStoredTileOrder: (value: string[]) => void;
    setStoredHintsUsed: (value: number) => void;
    setStoredLastHintUsed: (value: number) => void;
    getStoredElapsedTime: () => number;
    getStoredStreak: () => number;
    getStoredMaxStreak: () => number;
    getStoredWins: () => number;
    getStoredLatestDate: () => string;
    setStoredLatestDate: (value: string) => void;
    setStoredCompleted: (value: boolean) => void;
    getStoredTileOrder: () => string[];
    getStoredHintsUsed: () => number;
    getStoredLastHintUsed: () => number;
    getStoredMovesPlayed: () => number;
    setStoredStreak: (value: number) => void;
    setStoredMaxStreak: (value: number) => void;
    setStoredWins: (value: number) => void;
  };

  function getModeStorageFunctions(mode: string): StorageFunctions {
    const isClassic = mode === "classic";
    return {
      getStoredCompleted: isClassic ? getStoredClassicCompleted : getStoredCasualCompleted,
      setStoredElapsedTime: isClassic ? setStoredClassicElapsedTime : setStoredCasualElapsedTime,
      setStoredMovesPlayed: isClassic ? setStoredClassicMovesPlayed : setStoredCasualMovesPlayed,
      setStoredTileOrder: isClassic ? setStoredClassicTileOrder : setStoredCasualTileOrder,
      setStoredHintsUsed: isClassic ? setStoredClassicHintsUsed : setStoredCasualHintsUsed,
      setStoredLastHintUsed: isClassic ? setStoredClassicLastHintUsed : setStoredCasualLastHintUsed,
      getStoredElapsedTime: isClassic ? getStoredClassicElapsedTime : getStoredCasualElapsedTime,
      getStoredStreak: isClassic ? getStoredClassicStreak : getStoredCasualStreak,
      getStoredMaxStreak: isClassic ? getStoredClassicMaxStreak : getStoredCasualMaxStreak,
      getStoredWins: isClassic ? getStoredClassicWins : getStoredCasualWins,
      getStoredLatestDate: isClassic ? getStoredLatestClassicDate : getStoredLatestCasualDate,
      setStoredLatestDate: isClassic ? setStoredLatestClassicDate : setStoredLatestCasualDate,
      setStoredCompleted: isClassic ? setStoredClassicCompleted : setStoredCasualCompleted,
      getStoredTileOrder: isClassic ? getStoredClassicTileOrder : getStoredCasualTileOrder,
      getStoredHintsUsed: isClassic ? getStoredClassicHintsUsed : getStoredCasualHintsUsed,
      getStoredLastHintUsed: isClassic ? getStoredClassicLastHintUsed : getStoredCasualLastHintUsed,
      getStoredMovesPlayed: isClassic ? getStoredClassicMovesPlayed : getStoredCasualMovesPlayed,
      setStoredStreak: isClassic ? setStoredClassicStreak : setStoredCasualStreak,
      setStoredMaxStreak: isClassic ? setStoredClassicMaxStreak : setStoredCasualMaxStreak,
      setStoredWins: isClassic ? setStoredClassicWins : setStoredCasualWins
    };
  }

  function mulberry32(seed: number): () => number {
    let t = seed;
    return function () {
      t += 0x6D2B79F5;
      let r = Math.imul(t ^ (t >>> 15), t | 1);
      r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
  }
  
  function shuffleOrder<T>(array: T[], rng: () => number): T[] {
    const a = [...array];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  function initializeNewGame(mode: string) {
    const storage = getModeStorageFunctions(mode);

    storage.setStoredLatestDate(getTodaysDate());
    storage.setStoredCompleted(false);
    const seed = getTodaysDate().charCodeAt(getTodaysDate().length - 1);
    const rng = mulberry32(seed);
    const shuffledTiles = shuffleOrder(ALL_TILES, rng);
    setGameTiles(shuffledTiles);
    storage.setStoredTileOrder(shuffledTiles);
    setElapsedTime(0);
    storage.setStoredElapsedTime(0);
    setHintsUsed(0);
    storage.setStoredHintsUsed(0);
    setLastHintUsed(0);
    storage.setStoredLastHintUsed(0);
    setMovesPlayed(0);
    storage.setStoredMovesPlayed(0);
    trackEvent(`${mode} special game started`, { userId: getUserId(getStoredUserId, setStoredUserId) });
  }

  function resumeGame(mode: string) {
    const storage = getModeStorageFunctions(mode);
    setGameTiles(storage.getStoredTileOrder());
    setElapsedTime(storage.getStoredElapsedTime());
    setHintsUsed(storage.getStoredHintsUsed());
    setLastHintUsed(storage.getStoredLastHintUsed());
    setMovesPlayed(storage.getStoredMovesPlayed());
  }

  function play(mode: string) {
    setMode(mode);
    const storage = getModeStorageFunctions(mode);

    setSolutionTiles(WINNING_ORDER);
    setNumSolutions(1);

    if (storage.getStoredLatestDate() !== getTodaysDate()) {
      initializeNewGame(mode);
    } else {
      resumeGame(mode);
    }

    if (storage.getStoredCompleted()) {
      setModalState('victory');
    } else {
      setModalState('game');
    }
  }

  const [submitUserGameState] = useMutation(SUBMIT_USER_GAME, {
    onCompleted: (submitData) => {
      if (!submitData.submitUserGame.success) {
        trackEvent('special submission failed', {
          tiles: gameTiles,
          userId: getUserId(getStoredUserId, setStoredUserId),
          date: getTodaysDate()});
        return;
      }
      if (submitData.submitUserGame.solved) {
        const storage = getModeStorageFunctions(mode);
        storage.setStoredStreak(submitData.submitUserGame.streak);
        storage.setStoredMaxStreak(submitData.submitUserGame.maxStreak);
        storage.setStoredWins(submitData.submitUserGame.wins);
        storage.setStoredCompleted(true);
        offerClaimRef.current = submitData.submitUserGame.offerClaim && process.env.REACT_APP_PUZZLI_CLAIMS === "true"
        trackEvent('special victory', {
          userId: getUserId(getStoredUserId, setStoredUserId),
          movesPlayed: movesPlayed,
          elapsedTimeMs: elapsedTime, 
          elapsedTime: formatTime(elapsedTime), 
          date: getTodaysDate()});
      }
    }
  });

  useEffect(() => {
    let interval = setInterval(() => {
      if (modalState === 'game' && !getModeStorageFunctions(mode).getStoredCompleted()) {
        setElapsedTime((elapsedTime) => elapsedTime + 100);
      }
    }, 100);
    return () => {
      clearInterval(interval)
    }
  }, [modalState, mode]);

  useEffect(() => {
    if (elapsedTime >= 1000) {
      getModeStorageFunctions(mode).setStoredElapsedTime(elapsedTime);
    }
  }, [elapsedTime, mode]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    if (hintsUsed >= 1 && (gameTiles[4] === event.over?.id || event.over === null) ||
        hintsUsed >= 2 && (gameTiles[0] === event.over?.id || event.over === null) ||
        hintsUsed >= 3 && (gameTiles[8] === event.over?.id || event.over === null)) {
      setActiveId(null);
      return;
    }
    const { active, over } = event;
    setMovesPlayed(prev => {
      const newMoves = prev + 1;
      getModeStorageFunctions(mode).setStoredMovesPlayed(newMoves);
      if (newMoves === 20) {
        trackEvent('special 20_moves');
      }
      if (newMoves === 50) {
        trackEvent('special 50_moves');
      }
      return newMoves;
    });

    if (over !== null && active.id !== over?.id) {
      let nextTiles: string[] = null;
      setGameTiles((gameTiles) => {
        const oldIndex = gameTiles.findIndex((tile) => tile === active.id);
        const newIndex = gameTiles.findIndex((tile) => tile === over.id);

        nextTiles = arraySwap(gameTiles, oldIndex, newIndex);
        if (!getModeStorageFunctions(mode).getStoredCompleted()) {
          getModeStorageFunctions(mode).setStoredTileOrder(nextTiles);
          if (isWinningOrder(nextTiles)) {
            submitGame(nextTiles);
          }
        }
        return nextTiles;
      });
    }
    setActiveId(null);
  }, [hintsUsed, mode, gameTiles]);

  function isWinningOrder(tiles: string[]): boolean {
    return tiles.every((tile, index) => tile === WINNING_ORDER[index]);
  }

  function hint() {
    let nextTiles: string[] = null;
    getModeStorageFunctions(mode).setStoredHintsUsed(hintsUsed + 1);
    setHintsUsed(hintsUsed + 1);
    setLastHintUsed(elapsedTime);
    getModeStorageFunctions(mode).setStoredLastHintUsed(elapsedTime);
    trackEvent('special hint', {
      hints: hintsUsed, 
      userId: getUserId(getStoredUserId, setStoredUserId), 
      elapsedTimeMs: elapsedTime, 
      elapsedTime: formatTime(elapsedTime), 
      date: getTodaysDate()});
    setGameTiles((gameTiles) => {
      const indexToFind = hintsUsed === 0 ? 4 : hintsUsed === 1 ? 0 : 8;
      const solutionTile = solutionTiles[indexToFind];
      const oldIndex = gameTiles.findIndex(tile => tile === solutionTile)!;
      const newIndex = indexToFind; 

      nextTiles = arraySwap(gameTiles, oldIndex, newIndex);

      getModeStorageFunctions(mode).setStoredTileOrder(nextTiles);
      if (isWinningOrder(nextTiles)) {
        submitGame(nextTiles);
      }
      return nextTiles;
    });
  }

  function shuffle() {
    let nextTiles: string[] = null;
    setGameTiles((gameTiles) => {
      nextTiles = [...gameTiles];
      let currentIndex = nextTiles.length, temporaryValue, randomIndex;

      while (currentIndex !== 0) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;
    
        temporaryValue = nextTiles[currentIndex];
        nextTiles[currentIndex] = nextTiles[randomIndex];
        nextTiles[randomIndex] = temporaryValue;
      }

      if (hintsUsed > 2) {
        const oldIndex = nextTiles.findIndex(tile => tile === solutionTiles[8])!;
        const newIndex = 8;
        nextTiles = arraySwap(nextTiles, oldIndex, newIndex);
      }
      if (hintsUsed > 1) {
        const oldIndex = nextTiles.findIndex(tile => tile === solutionTiles[0])!;
        const newIndex = 0;
        nextTiles = arraySwap(nextTiles, oldIndex, newIndex);
      }
      if (hintsUsed > 0) {
        const oldIndex = nextTiles.findIndex(tile => tile === solutionTiles[4])!;
        const newIndex = 4;
        nextTiles = arraySwap(nextTiles, oldIndex, newIndex);
      }
      
      if (!getModeStorageFunctions(mode).getStoredCompleted()) {
        getModeStorageFunctions(mode).setStoredTileOrder(nextTiles);
      }
    
      return nextTiles;
    });
  }

  const triggerVictory = () => {
    confetti({
      particleCount: 500,
      spread: 360,
      origin: { x: 0.5, y: 0.5 }
    });
    const tiles = document.querySelectorAll('.tile');
    tiles.forEach((tile) => {
      setTimeout(() => {
        tile.classList.add('wave');
        setTimeout(() => {
          tile.classList.remove('wave');
        }, 1250);
      }, 0);
    });

    setTimeout(() => {
      if (offerClaimRef.current) {
        confetti({
          particleCount: 500,
          spread: 360,
          origin: { x: 0.5, y: 0.5 }
        });
        setModalState('offer');
      } else {
        setModalState('victory'); 
      }
    }, 1500)
  };

  const submitGame = (tiles: String[]) => {
    triggerVictory();
    submitUserGameState({
      variables: {
        userId: getUserId(getStoredUserId, setStoredUserId),
        date: activeGameDate || getTodaysDate(),
        elapsedTime: getModeStorageFunctions(mode).getStoredElapsedTime(),
        tiles: tiles,
        mode: mode
      }
    })
  }

  if (!gameExists || process.env.REACT_APP_PUZZLI_MAINTENANCE === "true") return <ErrorModal greetingIcon={greetingIconImage || ""}/>;

  const tiles = gameTiles.map(tile => (
    <SpecialSortableItem
      id={tile}
      key={tile}
      imageName={tile}
      activeId={activeId}
      disabled={
        (hintsUsed >= 1 && solutionTiles[4] === tile) ||
        (hintsUsed >= 2 && solutionTiles[0] === tile) ||
        (hintsUsed === 3 && solutionTiles[8] === tile)
      }
    />
  ));

  const hintDisabled = hintsUsed >= 3 || getModeStorageFunctions(mode).getStoredCompleted() || elapsedTime - lastHintUsed < hintLength * 1000;
  const streak = getModeStorageFunctions(mode).getStoredCompleted() ? 
    getModeStorageFunctions(mode).getStoredStreak() : 
    getModeStorageFunctions(mode).getStoredStreak() + 1;
  const wins = getModeStorageFunctions(mode).getStoredCompleted() ? 
    (getModeStorageFunctions(mode).getStoredWins() ?? getModeStorageFunctions(mode).getStoredMaxStreak() ?? streak) : 
    (getModeStorageFunctions(mode).getStoredWins() ?? getModeStorageFunctions(mode).getStoredMaxStreak() ?? streak) + 1;
  return (
    <div className="app-viewport">
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <GreetingModal 
          onGreetingClose={(mode: string) => {
            play(mode);
          }}
          showModal={modalState === 'greeting'}
          gameNumber={getGameNumber()} 
          greetingIcon={greetingIconImage || ""}
          disableButton={false}
          onAdminShortcut={goToAdminViaShortcut}>
        </GreetingModal>
        <HelpModal 
          onHelpClose={() => setModalState('game')}
          showModal={modalState === 'help'}
          howToImg={howToImg} mode={mode}>
        </HelpModal>
        <VictoryModal
          onVictoryClose={() => setModalState('game')}
          showModal={modalState === 'victory'}
          elapsedTime={formatTime(elapsedTime)}
          gameNumber={getGameNumber()} 
          streak={streak}
          maxStreak={streak > getModeStorageFunctions(mode).getStoredMaxStreak() ? streak : getModeStorageFunctions(mode).getStoredMaxStreak()}
          userId={getUserId(getStoredUserId, setStoredUserId)}
          wins={wins}
          hintsUsed={hintsUsed}
          gameTiles={WINNING_ORDER}
          mode={mode}
          onSwitchMode={() => {
            setModalState('game');
            play(mode === 'classic' ? 'casual' : 'classic');
          }}
          isSpecialPuzzle={true}
        >
        </VictoryModal>
        <OfferModal
          onOfferClose={() => setModalState('game')}
          showModal={modalState === 'offer'}
          maxStreak={streak > getModeStorageFunctions(mode).getStoredMaxStreak() ? streak : getModeStorageFunctions(mode).getStoredMaxStreak()}
          userId={getUserId(getStoredUserId, setStoredUserId)}>
        </OfferModal>
        {
        modalState !== 'greeting' &&
          <div>
            <div 
              style={{
                fontSize: window.innerWidth > 500 ? 100 : 80, 
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
              onClick={() => setModalState('greeting')}
            > 
              Puzzli 
            </div>
            <NavigationBar 
                onBackOpen={() => setModalState('greeting')}
                onHelpOpen={() => setModalState('help')}
                onStatsOpen={() => setModalState('victory')}
                completed={(getModeStorageFunctions(mode).getStoredCompleted())}
                elapsedTime={formatTime(elapsedTime)} helpIcon={helpIcon} igIcon={igIcon} statsIcon={statsIcon} backIcon={backIcon}/>
            {
              gameExists ?
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={gameTiles.map(tile => tile)} strategy={rectSwappingStrategy}>
                  <Grid columns={3}>
                    {tiles}
                  </Grid>
                </SortableContext>
                <DragOverlay>
                  {activeId ? (
                    <SpecialItem
                      imageName={activeId}
                      isDragging
                      disabled={false}
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>
              :
              <div style={{fontSize: 80}}> Error loading game. Check back soon! </div>
            }
            <div className="game-footer">
              <div className="game-solutions"> 
                Total Solutions: <span style={{ color: getSolutionsFontColor(numSolutions) }}>{numSolutions}</span> 
              </div>
              <div className="game-action-bar">
                <Button 
                  onClick={hint} 
                  className={hintDisabled ? 'game-button-disabled' : 'game-button'}
                  disabled={hintDisabled}>
                  Hint{getModeStorageFunctions(mode).getStoredCompleted() || hintsUsed >= 3 || elapsedTime - lastHintUsed >= hintLength * 1000 ? "" : " (" + (hintLength - Math.floor((elapsedTime - lastHintUsed)/1000)) + ")"}
                </Button>
                <Button onClick={shuffle} className={getModeStorageFunctions(mode).getStoredCompleted() ? 'game-button-disabled' : 'game-button'} disabled={getModeStorageFunctions(mode).getStoredCompleted()}>
                  Shuffle 
                </Button>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  );
}
